import { FileText, TrendingUp, ArrowUpDown, Eye, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGSCTopPages } from '@/hooks/useGSCData';
import { useGA4TopPages } from '@/hooks/useGA4Data';
import { useAuthStore } from '@/stores/auth-store';
import { useState, useMemo } from 'react';

type SortField = 'page' | 'clicks' | 'impressions' | 'ctr' | 'position' | 'pageviews' | 'bounceRate';
type SortDir = 'asc' | 'desc';

interface CombinedPageData {
  page: string;
  // GSC metrics
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
  // GA4 metrics
  pageviews?: number;
  avgDuration?: number;
  bounceRate?: number;
  // Combined
  hasGSC: boolean;
  hasGA4: boolean;
}

/**
 * Landing Pages View - Combined GSC + GA4 page performance
 * Shows pages with both search metrics and engagement metrics
 */
export function LandingPagesView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;
  const hasGA4 = !!activeProperty?.ga4Property;

  const { data: gscPages, isLoading: gscLoading } = useGSCTopPages(50);
  const { data: ga4Pages, isLoading: ga4Loading } = useGA4TopPages(50);

  const [sortField, setSortField] = useState<SortField>('clicks');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [searchFilter, setSearchFilter] = useState('');
  const [showOnlyMatched, setShowOnlyMatched] = useState(false);

  const isLoading = gscLoading || ga4Loading;

  // Combine GSC and GA4 data by matching URLs
  const combinedPages = useMemo(() => {
    const pageMap = new Map<string, CombinedPageData>();

    // Add GSC pages
    if (gscPages) {
      for (const page of gscPages) {
        // Normalize the page URL to just the path
        const pagePath = normalizePath(page.page);
        pageMap.set(pagePath, {
          page: pagePath,
          clicks: page.clicks,
          impressions: page.impressions,
          ctr: page.ctr,
          position: page.position,
          hasGSC: true,
          hasGA4: false,
        });
      }
    }

    // Merge GA4 pages
    if (ga4Pages) {
      for (const page of ga4Pages) {
        const pagePath = page.pagePath;
        const existing = pageMap.get(pagePath);
        if (existing) {
          existing.pageviews = page.pageviews;
          existing.avgDuration = page.avgDuration;
          existing.bounceRate = page.bounceRate;
          existing.hasGA4 = true;
        } else {
          pageMap.set(pagePath, {
            page: pagePath,
            pageviews: page.pageviews,
            avgDuration: page.avgDuration,
            bounceRate: page.bounceRate,
            hasGSC: false,
            hasGA4: true,
          });
        }
      }
    }

    return Array.from(pageMap.values());
  }, [gscPages, ga4Pages]);

  // Filter and sort
  const filteredPages = useMemo(() => {
    return combinedPages
      .filter((p) => {
        if (showOnlyMatched && (!p.hasGSC || !p.hasGA4)) return false;
        if (searchFilter && !p.page.toLowerCase().includes(searchFilter.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        const multiplier = sortDir === 'asc' ? 1 : -1;
        if (aVal === undefined && bVal === undefined) return 0;
        if (aVal === undefined) return 1;
        if (bVal === undefined) return -1;
        if (typeof aVal === 'string') {
          return aVal.localeCompare(bVal as string) * multiplier;
        }
        return ((aVal as number) - (bVal as number)) * multiplier;
      });
  }, [combinedPages, sortField, sortDir, searchFilter, showOnlyMatched]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Stats
  const matchedCount = combinedPages.filter((p) => p.hasGSC && p.hasGA4).length;
  const gscOnlyCount = combinedPages.filter((p) => p.hasGSC && !p.hasGA4).length;
  const ga4OnlyCount = combinedPages.filter((p) => !p.hasGSC && p.hasGA4).length;

  if (!hasGSC && !hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No property connected</p>
          <p className="text-sm mt-2">Connect GA4 or GSC to see landing page performance</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Landing Pages</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Analyze page performance from search to engagement
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard
          icon={FileText}
          label="Total Pages"
          value={combinedPages.length.toString()}
          subtitle="Tracked pages"
          color="purple"
        />
        <SummaryCard
          icon={TrendingUp}
          label="Matched"
          value={matchedCount.toString()}
          subtitle="GSC + GA4 data"
          color="purple"
        />
        <SummaryCard
          icon={Eye}
          label="GSC Only"
          value={gscOnlyCount.toString()}
          subtitle="Search data only"
          color="emerald"
        />
        <SummaryCard
          icon={Users}
          label="GA4 Only"
          value={ga4OnlyCount.toString()}
          subtitle="Engagement data only"
          color="blue"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Filter pages..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-surface border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showOnlyMatched}
            onChange={(e) => setShowOnlyMatched(e.target.checked)}
            className="rounded border-border"
          />
          <span className="text-sm text-muted-foreground">Show only matched pages</span>
        </label>
        <p className="text-sm text-muted-foreground">
          Showing {filteredPages.length} pages
        </p>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/50" />
          <span className="text-muted-foreground">GSC Data</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500/30 border border-blue-500/50" />
          <span className="text-muted-foreground">GA4 Data</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-purple-500/30 border border-purple-500/50" />
          <span className="text-muted-foreground">Both Sources</span>
        </div>
      </div>

      {/* Pages Table */}
      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <SortableHeader
                  field="page"
                  label="Page"
                  currentField={sortField}
                  currentDir={sortDir}
                  onSort={handleSort}
                  className="text-left"
                />
                {/* GSC Columns */}
                <th className="px-2 py-3" colSpan={4}>
                  <div className="text-xs font-medium text-emerald-400 uppercase tracking-wider text-center border-b border-emerald-500/30 pb-1 mb-1">
                    Search Console
                  </div>
                  <div className="flex">
                    <SortableHeaderCell field="clicks" label="Clicks" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                    <SortableHeaderCell field="impressions" label="Impr" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                    <SortableHeaderCell field="ctr" label="CTR" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                    <SortableHeaderCell field="position" label="Pos" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                  </div>
                </th>
                {/* GA4 Columns */}
                <th className="px-2 py-3" colSpan={3}>
                  <div className="text-xs font-medium text-blue-400 uppercase tracking-wider text-center border-b border-blue-500/30 pb-1 mb-1">
                    Analytics
                  </div>
                  <div className="flex">
                    <SortableHeaderCell field="pageviews" label="Views" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                    <SortableHeaderCell field="bounceRate" label="Bounce" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    <div className="animate-pulse">Loading pages...</div>
                  </td>
                </tr>
              ) : filteredPages.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    No pages found
                  </td>
                </tr>
              ) : (
                filteredPages.map((page, idx) => (
                  <PageRow key={idx} page={page} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function normalizePath(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname;
  } catch {
    // Already a path
    return url.startsWith('/') ? url : `/${url}`;
  }
}

interface SortableHeaderProps {
  field: SortField;
  label: string;
  currentField: SortField;
  currentDir: SortDir;
  onSort: (field: SortField) => void;
  className?: string;
}

function SortableHeader({ field, label, currentField, currentDir: _currentDir, onSort, className }: SortableHeaderProps) {
  const isActive = currentField === field;

  return (
    <th
      className={cn(
        'px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors',
        className
      )}
      onClick={() => onSort(field)}
    >
      <div className={cn('flex items-center gap-1', !className?.includes('text-left') && 'justify-center')}>
        {label}
        <ArrowUpDown
          className={cn(
            'h-3 w-3 transition-colors',
            isActive ? 'text-primary' : 'text-muted-foreground/50'
          )}
        />
      </div>
    </th>
  );
}

function SortableHeaderCell({ field, label, currentField, currentDir: _currentDir, onSort }: Omit<SortableHeaderProps, 'className'>) {
  const isActive = currentField === field;

  return (
    <div
      className="flex-1 px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors text-center"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center justify-center gap-1">
        {label}
        <ArrowUpDown
          className={cn(
            'h-2.5 w-2.5 transition-colors',
            isActive ? 'text-primary' : 'text-muted-foreground/50'
          )}
        />
      </div>
    </div>
  );
}

interface SummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  subtitle: string;
  color: 'emerald' | 'blue' | 'purple';
}

function SummaryCard({ icon: Icon, label, value, subtitle, color }: SummaryCardProps) {
  const colorStyles = {
    emerald: 'border-l-emerald-500 bg-emerald-500/5',
    blue: 'border-l-blue-500 bg-blue-500/5',
    purple: 'border-l-purple-500 bg-purple-500/5',
  };

  const iconStyles = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
  };

  return (
    <div className={cn('glass-panel p-4 border-l-4', colorStyles[color])}>
      <div className="flex items-center gap-2">
        <Icon className={cn('h-4 w-4', iconStyles[color])} />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
      <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
    </div>
  );
}

interface PageRowProps {
  page: CombinedPageData;
}

function PageRow({ page }: PageRowProps) {
  const rowColor = page.hasGSC && page.hasGA4
    ? 'bg-purple-500/5 hover:bg-purple-500/10'
    : page.hasGSC
      ? 'hover:bg-emerald-500/5'
      : 'hover:bg-blue-500/5';

  return (
    <tr className={cn('transition-colors', rowColor)}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <FileText className={cn(
            'h-4 w-4 flex-shrink-0',
            page.hasGSC && page.hasGA4 ? 'text-purple-400' :
              page.hasGSC ? 'text-emerald-400' : 'text-blue-400'
          )} />
          <span className="text-sm text-foreground truncate max-w-[250px]" title={page.page}>
            {page.page}
          </span>
        </div>
      </td>
      {/* GSC Data */}
      <td className="px-2 py-3 text-center" colSpan={4}>
        {page.hasGSC ? (
          <div className="flex">
            <div className="flex-1 px-2">
              <span className="text-sm font-medium text-foreground">{page.clicks?.toLocaleString() ?? '-'}</span>
            </div>
            <div className="flex-1 px-2">
              <span className="text-sm text-muted-foreground">{page.impressions?.toLocaleString() ?? '-'}</span>
            </div>
            <div className="flex-1 px-2">
              <span className={cn(
                'text-sm',
                (page.ctr ?? 0) > 5 ? 'text-success' : (page.ctr ?? 0) > 2 ? 'text-foreground' : 'text-warning'
              )}>
                {page.ctr?.toFixed(1) ?? '-'}%
              </span>
            </div>
            <div className="flex-1 px-2">
              <PositionBadge position={page.position ?? 0} />
            </div>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground/50">No search data</span>
        )}
      </td>
      {/* GA4 Data */}
      <td className="px-2 py-3 text-center" colSpan={3}>
        {page.hasGA4 ? (
          <div className="flex">
            <div className="flex-1 px-2">
              <span className="text-sm font-medium text-foreground">{page.pageviews?.toLocaleString() ?? '-'}</span>
            </div>
            <div className="flex-1 px-2">
              <span className={cn(
                'text-sm',
                (page.bounceRate ?? 0) < 40 ? 'text-success' : (page.bounceRate ?? 0) < 60 ? 'text-foreground' : 'text-warning'
              )}>
                {page.bounceRate?.toFixed(0) ?? '-'}%
              </span>
            </div>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground/50">No analytics data</span>
        )}
      </td>
    </tr>
  );
}

function PositionBadge({ position }: { position: number }) {
  const rounded = Math.round(position * 10) / 10;

  let colorClass = 'bg-muted text-muted-foreground';
  if (position <= 3) {
    colorClass = 'bg-success/20 text-success';
  } else if (position <= 10) {
    colorClass = 'bg-blue-500/20 text-blue-400';
  } else if (position <= 20) {
    colorClass = 'bg-warning/20 text-warning';
  }

  return (
    <span className={cn('inline-flex px-1.5 py-0.5 rounded text-xs font-medium', colorClass)}>
      {rounded}
    </span>
  );
}
